import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const GOLD = '#B8892A';
const GOLD_L = '#D4A843';
const INK = '#110E0A';
const MUTED = '#6B5E4E';
const BG = '#FAFAF8';
const BD = '#E8E0D0';
const TEAL = '#1A6B54';

// Fallback static houses (used while API loads)
const HOUSES_FALLBACK = [
  { code: 'おた', name: 'おたからや', jp: 'OTAKARAYA', tags: ['腕表', '珠宝', '名包'] },
  { code: '大吉', name: '大吉', jp: 'DAIKICHI', tags: ['腕表', '珠宝'] },
  { code: 'Eco', name: 'Eco Auc', jp: 'ECO AUCTION', tags: ['腕表', '珠宝', '名包'] },
  { code: 'RS', name: 'RS Auction', jp: 'RS AUCTION', tags: ['腕表', '珠宝'] },
  { code: 'RK', name: 'RK Auction', jp: 'RK AUCTION', tags: ['腕表', '名包'] },
  { code: 'APR', name: 'APRE', jp: 'APRE AUCTION', tags: ['腕表', '珠宝'] },
  { code: 'JWA', name: 'JWA', jp: 'JAPAN WATCH', tags: ['腕表', '珠宝'] },
  { code: 'RVA', name: 'REVA', jp: 'REVA AUCTION', tags: ['珠宝', '名包'] },
  { code: 'TL', name: 'Timeless', jp: 'TIMELESS', tags: ['腕表'] },
  { code: 'NJ', name: 'NJ Auction', jp: 'NJ AUCTION', tags: ['名包', '珠宝'] },
  { code: 'komehyo', name: 'Komehyo', jp: 'コメヒョー', tags: ['腕表', '珠宝', '名包'] },
  { code: 'naniwa', name: 'Naniwa', jp: 'ナニワ', tags: ['腕表', '珠宝', '名包'] },
  { code: 'daikokuya', name: '大黒屋', jp: 'DAIKOKUYA', tags: ['腕表', '珠宝', '名包'] },
  { code: 'tokioka', name: 'トキオカ', jp: 'TOKIOKA', tags: ['腕表', '珠宝'] },
  { code: 'yba', name: 'YBA', jp: 'YBA', tags: ['腕表', '珠宝'] },
  { code: 'gta', name: 'GTA', jp: 'GTA', tags: ['腕表', '珠宝'] },
  { code: 'tokioka', name: 'トキオカ', jp: 'TOKIOKA', tags: ['腕表', '珠宝'] },
  { code: 'yba', name: 'YBA', jp: 'YBA', tags: ['腕表', '珠宝'] },
  { code: 'gta', name: 'GTA', jp: 'GTA', tags: ['腕表', '珠宝'] },
];

const CAT_LABELS = { watch: '腕表', jewelry: '珠宝', bag: '名包', art: '艺术品' };

const STEPS = [
  { step: '1', title: '浏览拍品', desc: '在拍品一览中搜索心仪的腕表、珠宝、名包', color: GOLD },
  { step: '2', title: '提交委托', desc: '设定最高授权价，缴纳少量保证金（未中标全额退还）', color: TEAL },
  { step: '3', title: '自动竞拍', desc: '拍卖结束前系统精准出价，全程截图留证', color: '#E67E22' },
  { step: '4', title: '检品交付', desc: '专业五维检品评分，确认后灵活配送到手', color: '#2980B9' },
];

const FEATURES = [
  { icon: '🤖', title: '全自动竞拍', desc: '拍卖结束前精准出价，无需熬夜盯盘' },
  { icon: '🔒', title: '资金安全保障', desc: '保证金专户管理，未中标可退，透明可查' },
  { icon: '🔬', title: '专业检品报告', desc: '五维评分+高清实拍，品质透明' },
  { icon: '📦', title: '灵活交付', desc: '日本仓储或国际配送，自由选择' },
  { icon: '💱', title: '实时汇率', desc: '中行实时汇率结算，账单透明' },
  { icon: '📋', title: '全程可追踪', desc: '委托、竞拍、检品、物流状态实时可查' },
];

const TESTIMONIALS = [
  { name: '张女士', city: '上海', emoji: '👜', text: '通过云川中古拍到了一只梦寐以求的爱马仕铂金包，全程透明，检品报告很详细，非常放心。', item: '爱马仕 Birkin 30', stars: '★★★★★' },
  { name: '李先生', city: '北京', emoji: '⌚', text: '第一次拍日本腕表，客服全程中文指导，最终以合理价格拍到了百达翡丽，值得信赖！', item: '百达翡丽 Nautilus', stars: '★★★★★' },
  { name: '王女士', city: '成都', emoji: '💍', text: '已经通过云川中古拍了七八件了，每次都很顺利，保证金冻结解冻也很及时。', item: '卡地亚 Love 戒指', stars: '★★★★☆' },
];

export default function AuctionIntro() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [liveCount, setLiveCount] = useState(null);
  const [houses, setHouses] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    api.get('/lots', { params: { status: 'published', limit: 1 } })
      .then(r => setLiveCount(r.data?.total ?? r.data?.data?.length ?? 0))
      .catch(() => {});
    api.get('/auction-houses')
      .then(r => setHouses(r.data?.data || r.data || []))
      .catch(() => {});
    api.get('/sessions')
      .then(r => setSessions(r.data?.data || r.data || []))
      .catch(() => {});
  }, []);

  // Count active sessions per house
  const sessionCountByHouse = {};
  sessions.forEach(s => {
    const hid = s.house_id || s.house_name;
    if (hid) sessionCountByHouse[hid] = (sessionCountByHouse[hid] || 0) + 1;
  });

  // Use API houses if available, otherwise fallback
  const displayHouses = houses.length > 0 ? houses : HOUSES_FALLBACK;

  const px = isMobile ? '16px' : '24px';

  return (
    <div style={{ background: BG }}>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(160deg, #1A1410 0%, #2A2218 50%, #18140C 100%)',
        padding: isMobile ? '40px 0 48px' : '64px 0 72px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(184,137,42,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(184,137,42,.04) 1px,transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}`, position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 4, color: GOLD_L, textTransform: 'uppercase', marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 24, height: 1, background: GOLD_L }} />JAPAN AUCTION PROXY<span style={{ width: 24, height: 1, background: GOLD_L }} />
          </div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 26 : 42, fontWeight: 700, color: '#fff', letterSpacing: 3, lineHeight: 1.3, marginBottom: 12 }}>
            日本代拍 · 直连 <em style={{ color: GOLD_L, fontStyle: 'normal' }}>17</em> 家拍卖行
          </h1>
          <p style={{ fontSize: isMobile ? 13 : 15, color: 'rgba(255,255,255,.55)', lineHeight: 1.9, maxWidth: 600, margin: '0 auto 32px' }}>
            实时对接おたからや、大吉、Eco Auc 等日本顶级拍卖行。自动化竞价系统精准出价，全程截图留证，检品后灵活交付。
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
            <button onClick={() => navigate('/auction-hub')} style={{
              padding: '14px 36px', background: GOLD, border: 'none', color: '#fff',
              fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 600, borderRadius: 4, cursor: 'pointer',
              letterSpacing: 3, boxShadow: '0 4px 20px rgba(184,137,42,.4)',
            }}>浏览拍品</button>
            <button onClick={() => navigate('/sessions')} style={{
              padding: '14px 36px', background: 'transparent', border: `1px solid rgba(184,137,42,.4)`,
              color: GOLD_L, borderRadius: 4, cursor: 'pointer', fontSize: 15, letterSpacing: 1,
            }}>场次日历</button>
          </div>

          {/* Stats */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
            border: '1px solid rgba(184,137,42,.2)', borderRadius: 4, overflow: 'hidden',
            background: 'rgba(255,255,255,.03)', maxWidth: 480, margin: '0 auto',
          }}>
            {[
              { v: liveCount != null ? liveCount.toLocaleString() : '—', l: '正在拍卖' },
              { v: '17', l: '合作拍卖行' },
              { v: '24h', l: '极速响应' },
            ].map((s, i) => (
              <div key={s.l} style={{
                padding: '14px 12px', textAlign: 'center',
                borderRight: i < 2 ? '1px solid rgba(184,137,42,.15)' : 'none',
              }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 22, fontWeight: 600, color: GOLD_L, lineHeight: 1, marginBottom: 4 }}>{s.v}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', letterSpacing: 1 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: '#fff', padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 40 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 8 }}>HOW IT WORKS</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK, letterSpacing: 2 }}>代拍流程</h2>
          </div>
          <div style={{ maxWidth: 480, margin: '0 auto' }}>
            {STEPS.map((s, i) => (
              <div key={s.step} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: i < 3 ? 24 : 0, position: 'relative' }}>
                {i < 3 && <div style={{ position: 'absolute', left: 17, top: 38, width: 1, height: 'calc(100% - 10px)', background: BD }} />}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: s.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, fontFamily: 'var(--mono)', position: 'relative', zIndex: 1,
                }}>{s.step}</div>
                <div style={{ paddingTop: 6 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 4 }}>{s.title}</div>
                  <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.7 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ background: BG, padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 40 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 8 }}>FEATURES</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK, letterSpacing: 2 }}>平台优势</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: isMobile ? 12 : 20 }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{
                background: '#fff', border: `1px solid ${BD}`, borderRadius: 8, padding: isMobile ? 16 : 24,
              }}>
                <div style={{ fontSize: 24, marginBottom: 10 }}>{f.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 4 }}>{f.title}</div>
                <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.7 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auction Houses */}
      <section id="houses" style={{ background: '#fff', padding: isMobile ? '40px 0' : '56px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 32 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 8 }}>PARTNER AUCTION HOUSES</div>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 18 : 22, fontWeight: 700, color: INK, letterSpacing: 2 }}>
              直连日本 {displayHouses.length || 17} 家顶级拍卖行
            </h3>
            <p style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>点击拍卖行查看当前拍品</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: isMobile ? 8 : 12 }}>
            {houses.length > 0 ? houses.map(h => {
              const houseId = h.id || h.name;
              const sessCount = sessionCountByHouse[houseId] || sessionCountByHouse[h.name] || 0;
              // supported_categories 可能是数组(JSON 列)或逗号分隔字符串
              const _sc = h.supported_categories;
              const cats = (Array.isArray(_sc) ? _sc : String(_sc || '').split(',')).map(s => String(s).trim()).filter(Boolean);
              return (
                <div
                  key={houseId}
                  onClick={() => navigate('/auction-hub', { state: { houseId } })}
                  style={{
                    background: BG, border: `1px solid ${BD}`, borderRadius: 8,
                    padding: isMobile ? '12px 14px' : '16px 18px',
                    display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer',
                    transition: 'all .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 2px 12px rgba(184,137,42,.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 8, flexShrink: 0,
                    background: h.logo_url ? '#fff' : (h.color || INK),
                    border: `1px solid ${h.color ? h.color + '55' : BD}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    {h.logo_url
                      ? <img src={h.logo_url} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                      : <span style={{ fontFamily: 'var(--serif)', fontSize: 12, color: h.color ? '#fff' : GOLD_L, fontWeight: 600 }}>{h.emoji || (h.name_zh || h.name || '?').charAt(0)}</span>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.name_zh || h.name}
                    </div>
                    <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>
                      {cats.length > 0
                        ? cats.map(c => CAT_LABELS[c] || c).join(' · ')
                        : h.name_ja || h.name}
                    </div>
                    {sessCount > 0 && (
                      <div style={{ fontSize: 10, color: GOLD, fontWeight: 500, marginTop: 3 }}>
                        {sessCount} 场在线
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 14, color: BD, flexShrink: 0 }}>›</span>
                </div>
              );
            }) : HOUSES_FALLBACK.map(h => (
              <div
                key={h.code}
                onClick={() => navigate('/auction-hub')}
                style={{
                  background: BG, border: `1px solid ${BD}`, borderRadius: 8,
                  padding: isMobile ? '12px 14px' : '16px 18px',
                  display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer',
                  transition: 'all .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 2px 12px rgba(184,137,42,.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 8, background: INK, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--serif)', fontSize: 12, color: GOLD_L, fontWeight: 600,
                }}>{h.code}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>{h.name}</div>
                  <div style={{ fontSize: 10, color: MUTED }}>{h.tags.join(' · ')}</div>
                </div>
                <span style={{ fontSize: 14, color: BD, flexShrink: 0 }}>›</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: isMobile ? 16 : 24 }}>
            <button onClick={() => navigate('/auction-hub')} style={{
              padding: '10px 28px', background: 'transparent', border: `1px solid ${BD}`,
              color: MUTED, borderRadius: 4, cursor: 'pointer', fontSize: 13, letterSpacing: 1,
              transition: 'all .15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED; }}
            >查看全部拍品 →</button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ background: INK, padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 24 : 40 }}>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: '#fff', letterSpacing: 2 }}>他们的交易故事</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 12 : 20 }}>
            {TESTIMONIALS.map(t => (
              <div key={t.name} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(184,137,42,.15)', borderRadius: 4, padding: isMobile ? '18px 16px' : 24 }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 32, color: GOLD_L, lineHeight: 1, marginBottom: 10, opacity: 0.6 }}>"</div>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', lineHeight: 1.85, marginBottom: 14 }}>{t.text}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid rgba(255,255,255,.06)', paddingTop: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#F5EDD8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, border: '1px solid rgba(184,137,42,.3)', flexShrink: 0 }}>{t.emoji}</div>
                  <div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'rgba(255,255,255,.5)' }}>{t.name} · {t.city}</div>
                    <div style={{ fontSize: 11, color: GOLD_L, marginTop: 2 }}>{t.stars}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #FBF7F0 0%, #F0E8D8 100%)', padding: isMobile ? '48px 0' : '64px 0', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: `0 ${px}` }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 28, fontWeight: 700, color: INK, letterSpacing: 2, marginBottom: 12 }}>开始您的代拍之旅</h2>
          <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.8, marginBottom: 28 }}>注册后即可浏览全部拍品、提交竞拍委托，未中标保证金全额退还</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
            <button onClick={() => navigate('/auction-hub')} style={{
              padding: '14px 36px', background: GOLD, border: 'none', color: '#fff',
              fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, borderRadius: 4, cursor: 'pointer',
              letterSpacing: 2, boxShadow: '0 4px 20px rgba(184,137,42,.35)',
            }}>浏览拍品</button>
            <button onClick={() => navigate('/register')} style={{
              padding: '14px 36px', background: 'transparent', border: `1px solid ${BD}`,
              color: MUTED, borderRadius: 4, cursor: 'pointer', fontSize: 14,
            }}>免费注册</button>
          </div>
        </div>
      </section>
    </div>
  );
}
